import { useCallback } from 'react';

import { Form } from '@/modules/forms';
import { StaticModal, useModal } from '@/modules/modals';

import { useCreateVault, useUpdateVault } from '../../hooks';
import { vaultFormSchema, type VaultFormSchemaType, type VaultFormValues } from '../../schemas';
import type { VaultFormModalData } from '../../types';
import { vaultFormValuesToInput, vaultToFormValues } from '../../utils';
import { VaultForm } from '../VaultForm';

/**
 * Mounted once per page and opened through the registry, following `UpdateTagModal`. `Form` wraps the
 * modal rather than sitting inside it so the modal's own buttons can reach the form context.
 */
export const VaultFormModal = () => {
    const { modal, updateModal } = useModal<VaultFormModalData>(StaticModal.VAULT_FORM, { autoClose: true });

    const { mutateAsync: createVault } = useCreateVault();
    const { mutateAsync: updateVault } = useUpdateVault();

    const vault = modal?.data?.vault;

    const close = useCallback(() => {
        updateModal(false);
    }, [updateModal]);

    return (
        <Form<VaultFormSchemaType, VaultFormValues>
            schema={vaultFormSchema}
            defaultValues={vaultToFormValues(vault)}
            onSubmit={async values => {
                const input = vaultFormValuesToInput(values);

                try {
                    // Both member lists always travel with the name: sending an array replaces the whole
                    // list, so a partial send would half-update the vault.
                    await (vault ? updateVault({ vaultId: vault.id, input }) : createVault(input));
                } catch {
                    // The mutation hooks report the failure; rethrowing would only escape the submit handler.
                    return;
                }

                // Deliberately no navigation into the vault: nothing can put a record into one yet, so a
                // newly created one is necessarily empty.
                close();
            }}
        >
            <VaultForm open={modal?.visibility ?? false} vault={vault} onClose={close} />
        </Form>
    );
};
